import {
  type ClassroomScreenCard,
  ClassroomScreenScreen,
  ClassroomScreenPage,
} from "@/services/fobizzToolsApi";
import { Subscriber } from "@/types";
import merge from "lodash.merge";
import { defineStore } from "pinia";

interface Store {
  screen: ClassroomScreenScreen;
  subscribers: Subscriber[];
}

export const useClassroomScreenStore = defineStore("ClassroomScreen", {
  state: () => {
    return {
      screen: {} as ClassroomScreenScreen,
      subscribers: [],
    } as Store;
  },
  getters: {
    sortedPages: (state) => {
      if (!state.screen.pages) return;

      return state.screen.pages.sort((a, b) => (a.rank > b.rank ? 1 : -1));
    },
    findPageById: (state) => (id: string) =>
      state.screen.pages?.find((p) => p.id === id),
    firstPage: (state) => {
      if (!state.screen.pages?.length) return;

      return state.screen.pages[0];
    },
    pageAfterDelete: (state) => (page: ClassroomScreenPage) => {
      if (!state.screen.pages) return;

      const index = state.screen.pages.indexOf(page);
      if (index - 1 >= 0) {
        return state.screen.pages[index - 1];
      } else {
        return state.screen.pages[index + 1];
      }
    },
    pages: (state) => state.screen.pages,
    sortedCardsForPage: (state) => {
      return (pageId: string) => {
        if (!state.screen.pages) return;

        const page = state.screen.pages.find((b) => b.id === pageId);
        if (page) {
          return page.cards;
        } else {
          throw new Error("Page not found");
        }
      };
    },
    getSortedSubscribers: (state) => state.subscribers,
  },
  actions: {
    dynamicDispatch(action: string, payload: unknown) {
      const func = this[action as keyof unknown] as (
        ...args: unknown[]
      ) => void;
      if (func) {
        func(payload);
      } else {
        throw new Error("Unknown action: " + action);
      }
    },
    updateScreen(data: ClassroomScreenScreen) {
      merge(this.screen, data);
    },
    createPage(data: ClassroomScreenPage) {
      this.screen.pages?.push(data);
    },
    updatePage(data: ClassroomScreenPage) {
      if (!this.screen.pages) return;

      const page = this.screen.pages.find((b) => b.id === data.id);
      if (page) {
        merge(page, data);
        return page.cards;
      } else {
        throw new Error("Page not found");
      }
    },
    destroyPage(data: ClassroomScreenPage) {
      if (!this.screen.pages) return;

      const index = this.screen.pages.findIndex((p) => p.id === data.id);
      if (index !== -1) {
        this.screen.pages.splice(index, 1);
      } else {
        throw new Error("Page not found");
      }
    },
    createCard(data: ClassroomScreenCard) {
      const page = this.screen.pages?.find((b) => b.id === data.pageId);
      if (page) {
        page.cards.push(data);
      } else {
        throw new Error("Page not found");
      }
    },
    updateCard(data: ClassroomScreenCard) {
      if (!this.screen.pages) return;

      const page = this.screen.pages.find((p) => p.id === data.pageId);
      if (page) {
        const card = page.cards.find((c) => c.id === data.id);
        if (card) {
          merge(card, data);
        } else {
          throw new Error("Card not found");
        }
      } else {
        throw new Error("Page not found");
      }
    },
    moveToPage(data: ClassroomScreenCard, oldPageId: string) {
      if (!this.screen.pages) return;

      this.destroyCard({ ...data, pageId: oldPageId });
      this.createCard(data);
    },
    destroyCard(data: ClassroomScreenCard) {
      if (!this.screen.pages) return;

      const page = this.screen.pages.find((page) => {
        return page.cards.find((card) => {
          return card.id === data.id;
        });
      });

      if (page) {
        const index = page.cards.findIndex((c) => c.id === data.id);
        if (index !== -1) {
          page.cards.splice(index, 1);
        } else {
          throw new Error("Card not found");
        }
      } else {
        throw new Error("Page not found");
      }
    },
    setSubscribers(subscribers: Subscriber[]) {
      this.subscribers = subscribers;
    },
    addSubscriber(subscriber: Subscriber) {
      this.subscribers.push(subscriber);
    },
    removeSubscriber(subscriberId: string) {
      const index = this.subscribers.findIndex((s) => s.id === subscriberId);
      if (index !== -1) {
        this.subscribers.splice(index, 1);
      }
    },
    clearSubscribers() {
      this.subscribers = [];
    },
  },
});
